import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { WA_CATEGORIES, insertAtCursor } from "../lib/waTemplates";
import WaPlaceholderChips from "./WaPlaceholderChips";
import { WA_FIRMA_FIELDS } from "../lib/waLink";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Loader2, MessageCircle, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

// Impostazioni Studio → Messaggi WhatsApp: liste di messaggi preimpostati indipendenti per categoria.
export default function WaTemplatesSettings({ initial, onSaved }) {
  const [tpl, setTpl] = useState({});
  const [cat, setCat] = useState(WA_CATEGORIES[0].key);
  const [saving, setSaving] = useState(false);
  const [firma, setFirma] = useState("");
  const [firmaSel, setFirmaSel] = useState([]);
  const refs = useRef({});
  const lastIdx = useRef(null);
  useEffect(() => { api.get("/studio/me").then(({ data }) => { setFirma(data?.wa_signature?.text || ""); setFirmaSel(Array.isArray(data?.settings?.wa_firma_fields) ? data.settings.wa_firma_fields : []); }).catch(() => {}); }, []);
  const toggleFirma = async (key, on) => {
    const prev = firmaSel;
    const next = WA_FIRMA_FIELDS.map((f) => f.key).filter((k) => (k === key ? on : prev.includes(k)));
    setFirmaSel(next);
    try { await api.patch("/studio/settings", { wa_firma_fields: next }); toast.success("Firma studio WhatsApp aggiornata"); }
    catch (e) { setFirmaSel(prev); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { setTpl(initial && typeof initial === "object" ? initial : {}); }, [initial]);

  const list = Array.isArray(tpl[cat]) ? tpl[cat] : [];
  const setList = (next) => setTpl((t) => ({ ...t, [cat]: next }));
  const insertPh = (ph) => {
    let i = lastIdx.current;
    if (i == null || i >= list.length) {
      if (!list.length) { setList([ph]); lastIdx.current = 0; return; }
      i = list.length - 1;
    }
    setList(list.map((x, j) => (j === i ? insertAtCursor(refs.current[i], x, ph) : x)));
  };
  const save = async () => {
    setSaving(true);
    const clean = Object.fromEntries(Object.entries(tpl).map(([k, v]) => [k, (Array.isArray(v) ? v : []).map((s) => (s || "").trim()).filter(Boolean)]));
    try { await api.patch("/studio/settings", { wa_templates: clean }); setTpl(clean); onSaved?.(clean); toast.success("Messaggi WhatsApp salvati"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div id="sez-whatsapp" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="wa-templates-config">
      <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#25D366]" /> Messaggi WhatsApp</h3>
      <p className="text-sm text-slate-400 mt-1">Messaggi preimpostati per categoria di destinatario. Clicca un segnaposto per inserirlo nel punto del cursore del messaggio selezionato; <code className="text-amber-400">[saluto]</code> diventa Buongiorno, Buon pomeriggio, Buonasera o Buonanotte in base all'ora di invio.</p>
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="wa-tpl-categories">
        {WA_CATEGORIES.map((c) => {
          const n = (Array.isArray(tpl[c.key]) ? tpl[c.key] : []).filter((s) => (s || "").trim()).length;
          return (
            <button key={c.key} type="button" onClick={() => setCat(c.key)} data-testid={`wa-tpl-cat-${c.key}`}
              className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${cat === c.key ? "border-[#25D366] bg-[#25D366]/15 text-[#25D366]" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
              {c.label}{n ? ` (${n})` : ""}
            </button>
          );
        })}
      </div>
      <div className="mt-4"><WaPlaceholderChips onInsert={insertPh} testId="wa-tpl-ph" /></div>
      <div className="mt-3 space-y-2" data-testid="wa-tpl-list">
        {list.length === 0 && <p className="text-xs text-slate-500">Nessun messaggio preimpostato per questa categoria.</p>}
        {list.map((m, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <Textarea ref={(el) => { refs.current[i] = el; }} onFocus={() => { lastIdx.current = i; }} value={m} rows={2} onChange={(e) => setList(list.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`wa-tpl-${cat}-${i}`}
              className="flex-1 bg-slate-900/60 border-slate-700 text-sm" placeholder="Es. [saluto] [destinatario], in merito alla pratica n. [numero pratica]…" />
            <button type="button" onClick={() => setList(list.filter((_, j) => j !== i))} data-testid={`wa-tpl-del-${cat}-${i}`} title="Elimina"
              className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Button type="button" variant="outline" onClick={() => setList([...list, ""])} data-testid="wa-tpl-add" className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10"><Plus className="h-4 w-4 mr-1" /> Aggiungi messaggio</Button>
        <Button type="button" onClick={save} disabled={saving} data-testid="wa-tpl-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva</>}</Button>
      </div>
      <div className="mt-6 pt-4 border-t border-slate-800" data-testid="wa-firma-studio">
        <div className="text-xs font-semibold text-slate-300">Firma studio nei messaggi WhatsApp</div>
        <p className="text-xs text-slate-500 mt-0.5">I dati selezionati vengono inseriti tra il testo del messaggio e la firma pubblicitaria. Se non selezioni nulla, il blocco non appare.</p>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {WA_FIRMA_FIELDS.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <Checkbox checked={firmaSel.includes(f.key)} onCheckedChange={(v) => toggleFirma(f.key, !!v)} data-testid={`wa-firma-studio-${f.key}`} />
              {f.label}
            </label>
          ))}
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-slate-800" data-testid="wa-firma-pubblicitaria">
        <div className="text-xs font-semibold text-slate-300">Firma pubblicitaria</div>
        <div className="mt-1.5 rounded-md border border-slate-700 bg-slate-900/40 px-3 py-2 text-sm text-slate-400 whitespace-pre-wrap select-text" data-testid="wa-firma-text">{firma}</div>
      </div>
    </div>
  );
}
